import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Badge — small status pill. Replaces the dozens of one-off
// `<span class="rounded-full bg-emerald-50 …">` blocks scattered across
// the product. Tones are picked from a fixed palette so colour usage
// stays consistent across light + dark.
const badgeVariants = cva(
  [
    "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5",
    "text-[10.5px] font-medium uppercase tracking-[0.06em]",
    "ring-1 ring-inset",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3",
  ].join(" "),
  {
    variants: {
      tone: {
        neutral:
          "bg-muted text-muted-foreground ring-border",
        ink:
          "bg-foreground text-background ring-foreground/20",
        success:
          "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-400/30",
        warning:
          "bg-amber-500/10 text-amber-700 ring-amber-500/20 dark:text-amber-300 dark:ring-amber-400/30",
        info:
          "bg-blue-500/10 text-blue-700 ring-blue-500/20 dark:text-blue-300 dark:ring-blue-400/30",
        violet:
          "bg-violet-500/10 text-violet-700 ring-violet-500/20 dark:text-violet-300 dark:ring-violet-400/30",
        danger:
          "bg-red-500/10 text-red-700 ring-red-500/20 dark:text-red-300 dark:ring-red-400/30",
      },
      size: {
        sm: "h-5 px-2 text-[10px]",
        md: "h-6 px-2.5 text-[11px]",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "md",
    },
  },
);

export type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants>;

export function Badge({ className, tone, size, ...props }: BadgeProps) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ tone, size, className }))}
      {...props}
    />
  );
}

export { badgeVariants };
